import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import "./PaymentCallBack.css"; // reuse the same CSS

const PaymentOutcome = () => {
  const navigate = useNavigate();
  const { currentUser, userSettings, loading } = useAuth();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const planStatus = userSettings?.planStatus || "";
  const planKey = userSettings?.planKey || "";
  const paystackReference = userSettings?.paystackReference || userSettings?.lastPaymentReference || "";

  const settingsRef = useMemo(() => {
    if (!currentUser) return null;
    return doc(db, "users", currentUser.uid, "settings", "main");
  }, [currentUser]);

  // ✅ Safety: if user is not logged in, go login
  if (!loading && !currentUser) {
    navigate("/authentication", { replace: true });
    return null;
  }

  // ✅ Optional: if userSettings not loaded yet
  if (loading || !userSettings) {
    return (
      <main className="home">
        <section className="checkout payment-callback">
          <div className="checkout-header">
            <h2>
              Payment <span className="gradient-text">Outcome</span>
            </h2>
            <p className="checkout-subtitle">Loading your payment status...</p>
          </div>
          <div className="checkout-grid payment-grid">
            <div className="checkout-card featured payment-card">
              <p className="success">Please wait...</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const handleProceed = async () => {
    if (!settingsRef) return;
    try {
      setBusy(true);
      setError("");

      await updateDoc(settingsRef, {
        plan: "paid",
        planStatus: "active",
        updatedAt: serverTimestamp(),
      });

      navigate("/workspace", { replace: true }); // Home
    } catch (e) {
      setError(e?.message || "Could not activate your plan. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async () => {
    if (!settingsRef) return;
    try {
      setBusy(true);
      setError("");

      await updateDoc(settingsRef, {
        plan: "paid",
        planStatus: "pending",
        updatedAt: serverTimestamp(),
      });

      if (!planKey) {
        navigate("/pricing", { replace: true });
        return;
      }

      navigate(`/checkout?plan=${encodeURIComponent(planKey)}`, { replace: true });
    } catch (e) {
      setError(e?.message || "Could not start retry. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleSkipForNow = async () => {
    if (!settingsRef) return;
    try {
      setBusy(true);
      setError("");

      await updateDoc(settingsRef, {
        planKey: "free",
        plan: "free",
        planStatus: "active",
        billingCycle: "monthly",
        updatedAt: serverTimestamp(),
      });

      navigate("/", { replace: true }); // Home
    } catch (e) {
      setError(e?.message || "Could not switch to free plan. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const goBackToPricing = () => navigate("/pricing", { replace: true });

  const showSuccess = planStatus === "success";
  const showFailed = planStatus === "failed";
  
  return (
    <main className="home">
      <section className="checkout payment-callback">
        <div className="checkout-header">
          <h2>
            Payment <span className="gradient-text">Outcome</span>
          </h2>
          <p className="checkout-subtitle">
            This page shows the result of your payment. Choose what to do next.
          </p>
        </div>

        <div className="checkout-grid payment-grid">
          <div className="checkout-card featured payment-card">
            <h3>Payment Result</h3>

            {error && (
              <div className="payment-state">
                <p className="error">{error}</p>
              </div>
            )}

            {/* ✅ SUCCESS */}
            {showSuccess && (
              <div className="payment-state">
                <div className="payment-badge success-badge">Payment Successful</div>
                <p className="payment-title">Congratulations 🎉</p>
                <p className="payment-text">
                  Your payment has been confirmed. Click <b>Proceed</b> to activate your plan.
                </p>

                <div className="payment-actions">
                  <button className="primary-btn" type="button" onClick={handleProceed} disabled={busy}>
                    {busy ? "Please wait..." : "Proceed"}
                  </button>
                </div>
              </div>
            )}

            {/* ✅ FAILED */}
            {showFailed && (
              <div className="payment-state">
                <div className="payment-badge fail-badge">Payment Failed</div>
                <p className="payment-title">We couldn’t confirm your payment</p>
                <p className="payment-text">
                  Your payment did not complete successfully. You can retry, skip for now, or return to pricing.
                </p>

                <div className="payment-actions">
                  <button className="primary-btn" type="button" onClick={handleRetry} disabled={busy}>
                    {busy ? "Please wait..." : "Retry Payment"}
                  </button>
                  <button className="secondary-btn" type="button" onClick={handleSkipForNow} disabled={busy}>
                    Skip for now
                  </button>
                  <button className="secondary-btn" type="button" onClick={goBackToPricing} disabled={busy}>
                    Back to pricing
                  </button>
                </div>
              </div>
            )}

            {/* ✅ If status is something else */}
            {!showSuccess && !showFailed && (
              <div className="payment-state">
                <p className="muted">
                  Your current status is: <b>{planStatus || "—"}</b>
                </p>
                <p className="payment-text">
                  If you were expecting a result, please return to pricing and try again.
                </p>
                <div className="payment-actions">
                  <button className="primary-btn" type="button" onClick={goBackToPricing} disabled={busy}>
                    Back to pricing
                  </button>
                </div>
              </div>
            )}

            <div className="payment-meta">
              <div className="mini-row">
                <span>Plan</span>
                <span className="muted">{planKey || "—"}</span>
              </div>
              <div className="mini-row">
                <span>Status</span>
                <span className="muted">{planStatus || "—"}</span>
              </div>
              <div className="mini-row">
                <span>Reference</span>
                <span className="muted">{paystackReference || "—"}</span>
              </div>
            </div>

            <p className="tiny payment-footnote">
              You won’t be redirected automatically. You are in control of what happens next.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PaymentOutcome;
